import axios from 'axios';
import LocalStorageService from "Services/LocalStorageService";
import { REFRESH_TOKEN } from "../store/UrlConstants";

const localStorageService = LocalStorageService.getService();

// Configuration for rate limit handling
const RATE_LIMIT_CONFIG = {
  maxRetries: 5,
  initialDelayMs: 1000, // Start with a 1 second delay
  backoffFactor: 2 // Exponential backoff factor
};

// Create axios instances with withCredentials set to true by default
const baseApi = axios.create();

const refreshApi = axios.create();

// Queue for storing failed requests to retry after token refresh
let requestQueue = [];
let isRefreshing = false;

// Process the queue of failed requests
const processQueue = (error = null) => {
  requestQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else {
      resolve(baseApi(config));
    }
  });
  
  // Reset the queue
  requestQueue = [];
};

// Response interceptor for handling token refresh and rate limiting
baseApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 429 Too Many Requests (Rate Limiting)
    if (error.response?.status === 429) {
      // Initialize retry count if not already set
      originalRequest._retryCount = originalRequest._retryCount || 0;
      
      // Check if we've reached the maximum number of retries
      if (originalRequest._retryCount >= RATE_LIMIT_CONFIG.maxRetries) {
        console.error('Maximum retry attempts reached for rate limited request');
        return Promise.reject(error);
      }
      
      // Increment the retry count
      originalRequest._retryCount++;
      
      // Calculate delay with exponential backoff
      const delay = RATE_LIMIT_CONFIG.initialDelayMs * 
        Math.pow(RATE_LIMIT_CONFIG.backoffFactor, originalRequest._retryCount - 1);
      
      console.log(`Rate limited. Retrying request after ${delay}ms (Attempt ${originalRequest._retryCount} of ${RATE_LIMIT_CONFIG.maxRetries})`);
      
      // Wait for the calculated delay
      return new Promise(resolve => {
        setTimeout(() => {
          resolve(baseApi(originalRequest));
        }, delay);
      });
    }
    
    // If the error is not 401 or the request has already been retried for auth, reject
    if (error.response?.status !== 401 || originalRequest._authRetry) {
      return Promise.reject(error);
    }
    
    // Mark this request as retried for auth to prevent infinite loops
    originalRequest._authRetry = true;
    
    
    // Check if we're already refreshing
    if (!isRefreshing) {
      isRefreshing = true;
      localStorageService.setRefreshingToken('refreshingInProgress');
      
      try {
        // Attempt to refresh the token
        await refreshApi.get(REFRESH_TOKEN);
        
        // Token refresh successful
        localStorageService.setRefreshingToken('refreshDone');
        
        // Process all queued requests with the new token
        processQueue();
        
        // Reset refresh state
        isRefreshing = false;
        
        // Retry the original request
        return baseApi(originalRequest);
      } catch (refreshError) {
        // Token refresh failed
        isRefreshing = false;
        
        // Reject all queued requests
        processQueue(new Error('failed_refresh_auth'));
        
        return Promise.reject(refreshError);
      }
    }
    
    // If we're already refreshing, add this request to the queue
    return new Promise((resolve, reject) => {
      requestQueue.push({ resolve, reject, config: originalRequest });
    });
  }
);

export default baseApi;
